// Report-param grammar for the brand-perceptual-mapping block, ported from the coffee block
// (importReportParams / update*Params / importSharedSortParams).
//
// The bpm<key> section is a `!`-joined list of single-letter tokens whose first token is the
// active mode (`mq` / `mt` / `mm`); the rest are that mode's values. The sort order lives in the
// shared s<key> section, which the tv mode also mirrors into its own segment.

export const DEFAULT_SORT_ORDER = 'iasc'

const positions = items => items.map(item => `${item.position}`)

export const calcDefaultValues = ({ questionVariants = [], batches = [], defaultPlotByValue } = {}) => ({
  q: { b: positions(questionVariants), x: '1', y: '2', r: 'none', c: 'none' },
  t: {
    p: defaultPlotByValue || 'e',
    e: { e: '-1', a: '-2' },
    b: positions(questionVariants),
    a: positions(batches)
  },
  m: { b: positions(questionVariants), a: positions(batches) }
})

// Each token is a one-letter name followed by its value.
const parseSection = section =>
  (undefined === section ? [] : String(section).split('!')).reduce((acc, token) => {
    if (token) acc[token[0]] = token.slice(1)
    return acc
  }, {})

// Keeps only the values that still exist on the question; an empty result falls back to the
// default and is reported as an error, which makes the caller rewrite the section.
const resolveList = (stored, defaults, known) => {
  const list = undefined === stored ? defaults : String(stored).split('.')
  const kept = list.filter(value => known.includes(value))

  if (!kept.length) return { value: defaults, error: true }

  return kept.length === list.length ? { value: list, error: false } : { value: kept, error: true }
}

const resolveOption = (stored, fallback, options) =>
  options.some(option => stored === option.value) ? { value: stored, error: false } : { value: fallback, error: true }

const importQuadrant = (tokens, { defaultValues, questionVariants, selectOptions, selectOptionsWithNone }) => {
  const defaults = defaultValues.q
  const known = positions(questionVariants)

  const brands = resolveList(tokens.b, defaults.b, known)
  const x = resolveOption(tokens.x || defaults.x, defaults.x, selectOptions)
  const y = resolveOption(tokens.y || defaults.y, defaults.y, selectOptions)
  const r = resolveOption(tokens.r || defaults.r, defaults.r, selectOptionsWithNone)
  const c = resolveOption(tokens.c || defaults.c, defaults.c, selectOptionsWithNone)

  return {
    state: {
      quadrantBrandsValue: brands.value,
      quadrantXAxisValue: x.value,
      quadrantYAxisValue: y.value,
      quadrantRadiusValue: r.value,
      quadrantColorValue: c.value
    },
    error: brands.error || x.error || y.error || r.error || c.error
  }
}

const importTv = (tokens, { defaultValues, questionVariants, batches }) => {
  const defaults = defaultValues.t

  const plotBy = ['e', 'a'].includes(tokens.p || defaults.p)
    ? { value: tokens.p || defaults.p, error: false }
    : { value: defaults.p, error: true }

  const knownElevations = positions('e' === plotBy.value ? questionVariants : batches)
  const storedElevation = tokens.e || defaults.e[plotBy.value]
  const elevation =
    storedElevation === defaults.e[plotBy.value] || knownElevations.includes(storedElevation)
      ? { value: storedElevation, error: false }
      : { value: defaults.e[plotBy.value], error: true }

  const brands = resolveList(tokens.b, defaults.b, positions(questionVariants))
  const attributes = resolveList(tokens.a, defaults.a, positions(batches))

  return {
    state: {
      // the bpm<key> s-segment, which is the only place this block's sort order lives
      sortOrder: undefined === tokens.s ? DEFAULT_SORT_ORDER : tokens.s,
      tvPlotByValue: plotBy.value,
      tvElevationsRepresentValue: elevation.value,
      tvBrandsValue: brands.value,
      tvAttributesValue: attributes.value
    },
    error: plotBy.error || elevation.error || brands.error || attributes.error
  }
}

const importMds = (tokens, { defaultValues, questionVariants, batches }) => {
  const defaults = defaultValues.m

  const brands = resolveList(tokens.b, defaults.b, positions(questionVariants))
  const attributes = resolveList(tokens.a, defaults.a, positions(batches))

  return {
    state: { mdsBrandsValue: brands.value, mdsAttributesValue: attributes.value },
    error: brands.error || attributes.error
  }
}

const IMPORTERS = { q: importQuadrant, t: importTv, m: importMds }

// The section only ever carries the values of the mode that was active when it was written, so a
// sub-mode opened for the first time resolves from its own defaults rather than reading another
// mode's tokens.
export const resolveModeState = ({ mode, ...context } = {}) => IMPORTERS[mode]?.({}, context)?.state || {}

// `needsUpdate` marks a section the block cannot read back exactly, so the caller rewrites it.
export const resolveReportParams = ({ section, modes = [], defaultMode, ...context } = {}) => {
  const tokens = parseSection(section)
  const mode = tokens.m || defaultMode

  if (!modes.includes(mode)) {
    // an unknown mode falls back to the question default and rewrites the mode segment alone
    return { state: { mode: defaultMode }, needsUpdate: true, modeOnly: true }
  }

  const imported = IMPORTERS[mode](tokens, context)

  return { state: { mode, ...imported.state }, needsUpdate: imported.error, modeOnly: false }
}

const listToken = (name, value, defaults) =>
  value?.length && value.length < defaults.length ? `${name}${value.join('.')}` : null

const quadrantTokens = (state, { defaultValues, questionVariants }) => {
  const defaults = defaultValues.q

  return [
    listToken('b', state.quadrantBrandsValue, questionVariants),
    state.quadrantXAxisValue !== defaults.x ? `x${state.quadrantXAxisValue}` : null,
    state.quadrantYAxisValue !== defaults.y ? `y${state.quadrantYAxisValue}` : null,
    state.quadrantRadiusValue !== defaults.r ? `r${state.quadrantRadiusValue}` : null,
    state.quadrantColorValue !== defaults.c ? `c${state.quadrantColorValue}` : null
  ]
}

const tvTokens = (state, { defaultValues, questionVariants, batches }) => {
  const defaults = defaultValues.t

  return [
    state.tvPlotByValue !== defaults.p ? `p${state.tvPlotByValue}` : null,
    state.tvElevationsRepresentValue !== defaults.e[state.tvPlotByValue]
      ? `e${state.tvElevationsRepresentValue}`
      : null,
    listToken('b', state.tvBrandsValue, questionVariants),
    listToken('a', state.tvAttributesValue, batches),
    `s${state.sortOrder}`
  ]
}

const mdsTokens = (state, { questionVariants, batches }) => [
  listToken('b', state.mdsBrandsValue, questionVariants),
  listToken('a', state.mdsAttributesValue, batches)
]

const TOKENIZERS = { q: quadrantTokens, t: tvTokens, m: mdsTokens }

// The bpm<key> section: the active mode plus whatever that mode has moved off its defaults. A
// section that says nothing beyond the mode is dropped when the mode is the question default.
export const calcReportParamsSection = ({ state = {}, defaultMode, ...context } = {}) => {
  const { mode } = state
  const written = [`m${mode}`, ...TOKENIZERS[mode](state, context).filter(Boolean)]

  const section = written.join('!')
  const bare = 't' === mode ? [`m${mode}`, `m${mode}!s${DEFAULT_SORT_ORDER}`] : [`m${mode}`]

  if (bare.includes(section)) return mode === defaultMode ? null : section

  return section
}

// The mode-only write, used when an unreadable section is corrected.
export const calcModeParamsSection = ({ mode, defaultMode } = {}) => (mode === defaultMode ? null : `m${mode}`)

// The report-export payload, read by the Summary and PowerPoint exporters: it carries every
// setting the stats service builds its tables from, defaults included, in the form it reads them.
export const calcExportData = ({ state = {} } = {}) => {
  const { mode } = state
  const modeNames = { m: 'mds', t: 'tv', q: 'quadrant' }

  const data = { mode: modeNames[mode] }

  // the entity and attribute lists go over as the positions picked, the whole list included
  if ('q' === mode) {
    data.quadrantBrandsValue = state.quadrantBrandsValue
    data.quadrantXAxisValue = state.quadrantXAxisValue
    data.quadrantYAxisValue = state.quadrantYAxisValue
    data.quadrantRadiusValue = state.quadrantRadiusValue
    data.quadrantColorValue = state.quadrantColorValue
  }

  if ('t' === mode) {
    // only the thermometer carries a sort of its own
    data.sortOrder = state.sortOrder
    data.tvPlotByValue = state.tvPlotByValue
    data.tvElevationsRepresentValue = state.tvElevationsRepresentValue
    data.tvBrandsValue = state.tvBrandsValue
    data.tvAttributesValue = state.tvAttributesValue
  }

  if ('m' === mode) {
    data.mdsBrandsValue = state.mdsBrandsValue
    data.mdsAttributesValue = state.mdsAttributesValue
  }

  return data
}
